import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { IconDotLarge } from "@/assets/icons/geist/IconDotLarge";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { IconWarningCircleFilled } from "@/assets/icons/geist/IconWarningCircleFilled";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { TreeList, type TreeNode, type TreeListRef, type FlatTreeNode } from "@/components/TreeList";
import { duration, formatMediumTime } from "@/format/time";
import { useStreamLogs } from "@/hooks/use-stream-logs";
import { type PlainEnvironment } from "@/queries/environment-queries";
import {
    type LogGroup,
    NO_LOG_GROUP,
    type SectionLogLine,
    type LegacyLogLine,
} from "@/routes/environments/log-streams/log-groups";
import { formatError } from "@/utils/errors";
import Anser from "anser";
import { LogSectionOutcome } from "gitpod-next-api/gitpod/v1/environment_logs_pb";
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useThrottledCallback } from "use-debounce";

type LogLineNodeData = {
    type: "logLine";
    line: SectionLogLine | LegacyLogLine;
    lineIndex: number;
};

type LogGroupNodeData = {
    type: "logGroup";
    logGroup: LogGroup;
    validLines: (SectionLogLine | LegacyLogLine)[];
};

type LogTreeNodeData = LogLineNodeData | LogGroupNodeData;

type EnvironmentLogsProps = {
    environment?: PlainEnvironment;
    filterLogLinesWithoutTimestamps: boolean;
    logsUrl?: string;
    overscanCount?: number;
};

export const EnvironmentLogs: FC<EnvironmentLogsProps & PropsWithClassName> = ({
    className,
    environment,
    logsUrl,
    filterLogLinesWithoutTimestamps,
    overscanCount,
}) => {
    const logContainerRef = useRef<HTMLDivElement>(null);
    const treeListRef = useRef<TreeListRef>(null);
    const [isAutoScroll, setIsAutoScroll] = useState(true);
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const [availableHeight, setAvailableHeight] = useState(400);
    const [availableWidth, setAvailableWidth] = useState<number>();
    const [copiedLineIndex, setCopiedLineIndex] = useState<string | null>(null);
    const [copiedGroupId, setCopiedGroupId] = useState<string | null>(null);
    const { toast } = useToast();
    const location = useLocation();

    const { logGroups, loadingMessage, error } = useStreamLogs({ environment, logsUrl, maxLinesPerGroup: 10000 });

    // Handle auto-scroll with throttling
    const debouncedScrollToItem = useThrottledCallback(
        (
            nodeId: string,
            align?: "center" | "end" | "start" | "auto" | "smart",
            behavior?: "auto" | "instant" | "smooth",
        ) => {
            if (treeListRef.current) {
                treeListRef.current.scrollToItem(nodeId, align, behavior);
            }
        },
        500,
    );

    const updateAvailableSize = useThrottledCallback((w: number, h: number) => {
        setAvailableWidth(w);
        setAvailableHeight(h);
    }, 500);

    useEffect(() => {
        if (!logContainerRef.current) return;

        const resizeObserver = new ResizeObserver((_entries) => {
            if (logContainerRef.current) {
                updateAvailableSize(logContainerRef.current.clientWidth, logContainerRef.current.clientHeight);
            }
        });

        resizeObserver.observe(logContainerRef.current);
        updateAvailableSize(logContainerRef.current.clientWidth, logContainerRef.current.clientHeight);

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLegacyRunner =
        logGroups && Object.keys(logGroups).length === 1 && logGroups[NO_LOG_GROUP]?.lines?.length > 0;

    // Convert log groups to tree structure
    const treeNodes = useMemo((): TreeNode<LogTreeNodeData>[] => {
        if (!logGroups) return [];

        const legacyLogLines = (logGroups[NO_LOG_GROUP]?.lines ?? []).filter(
            (line): line is SectionLogLine | LegacyLogLine =>
                (line.type === "SectionLogLine" || line.type === "LegacyLogLine") &&
                (!filterLogLinesWithoutTimestamps || !!line.timestamp),
        );

        if (isLegacyRunner) {
            // For legacy runner, create flat list of log lines
            return legacyLogLines.map((line, index) => ({
                id: `legacy-line-${index}`,
                label: line.line,
                data: {
                    type: "logLine",
                    line,
                    lineIndex: index,
                } as LogLineNodeData,
            }));
        }

        // For modern runner, create tree with groups and their lines
        return Object.values(logGroups)
            .filter((logGroup) => logGroup.id !== NO_LOG_GROUP)
            .map((logGroup) => {
                const validLines = logGroup.lines.filter(
                    (line): line is SectionLogLine | LegacyLogLine =>
                        (line.type === "SectionLogLine" || line.type === "LegacyLogLine") &&
                        (!filterLogLinesWithoutTimestamps || !!line.timestamp),
                );

                const isDeepLinkToThisGroup = location.hash === `#${logGroup.id}`;
                const hasDeepLinkToAnotherGroup = location.hash && !isDeepLinkToThisGroup;
                const isInProgress = logGroup.outcome === undefined;
                const isContinuous = logGroup.continuous;
                const failedOrCancelled =
                    logGroup.outcome === LogSectionOutcome.FAILURE || logGroup.outcome === LogSectionOutcome.CANCELLED;

                const isExpanded =
                    !hasDeepLinkToAnotherGroup &&
                    !isContinuous &&
                    (isInProgress || failedOrCancelled || isDeepLinkToThisGroup);

                return {
                    id: logGroup.id,
                    label: logGroup.title,
                    isExpanded,
                    data: {
                        type: "logGroup",
                        logGroup,
                        validLines,
                    } as LogGroupNodeData,
                    children: validLines.map((line, index) => ({
                        id: `${logGroup.id}-line-${index}`,
                        label: line.line,
                        data: {
                            type: "logLine",
                            line,
                            lineIndex: index,
                        } as LogLineNodeData,
                    })),
                };
            });
    }, [logGroups, filterLogLinesWithoutTimestamps, isLegacyRunner, location.hash]);

    const handleOnRowsRendered = useCallback(
        (event: {
            scrollTop: number;
            scrollLeft: number;
            scrollHeight: number;
            scrollWidth: number;
            width: number;
            height: number;
            startIndex: number;
            stopIndex: number;
            nodes: FlatTreeNode<LogTreeNodeData>[];
        }) => {
            setLastScrollTop(event.scrollTop);
            if (event.scrollTop < lastScrollTop) {
                setIsAutoScroll(false);
                debouncedScrollToItem.cancel();
            } else if (
                !isAutoScroll &&
                event.scrollTop > lastScrollTop &&
                event.scrollTop + event.height >= event.scrollHeight - 16
            ) {
                setIsAutoScroll(true);
            } else if (isAutoScroll && event.scrollTop + event.height < event.scrollHeight) {
                debouncedScrollToItem(event.nodes[event.nodes.length - 1].id, "end", "smooth");
            }
        },
        [lastScrollTop, isAutoScroll, debouncedScrollToItem],
    );

    // Copy handlers
    const handleCopyLine = useCallback(
        async (line: string, nodeId: string) => {
            setCopiedLineIndex(nodeId);
            try {
                await navigator.clipboard.writeText(line);
                toast({ title: "Copied to clipboard" });
            } catch (error) {
                setCopiedLineIndex(null);
                toast({
                    title: "Failed to copy to clipboard",
                    description: formatError(error),
                });
            } finally {
                setTimeout(() => {
                    setCopiedLineIndex((prev) => (prev === nodeId ? null : prev));
                }, 2000);
            }
        },
        [toast],
    );

    const handleCopyGroup = useCallback(
        async (validLines: (SectionLogLine | LegacyLogLine)[], groupId: string) => {
            setCopiedGroupId(groupId);
            try {
                const textToCopy = validLines.map((l) => l.line).join("\n");
                await navigator.clipboard.writeText(textToCopy);
                toast({ title: "Copied to clipboard" });
            } catch (err) {
                setCopiedGroupId(null);
                toast({
                    title: "Failed to copy to clipboard",
                    description: formatError(err),
                });
            } finally {
                setTimeout(() => setCopiedGroupId(null), 2000);
            }
        },
        [toast],
    );

    // Get initial expanded node IDs based on tree structure
    const initialExpandedNodeIds = useMemo(() => {
        const expanded = new Set<string>();
        treeNodes.forEach((node) => {
            if (node.isExpanded) {
                expanded.add(node.id);
            }
        });
        return expanded;
    }, [treeNodes]);

    return (
        <div
            className="relative flex h-full w-full flex-col overflow-hidden rounded-xl bg-always-dark py-4 text-always-light"
            data-testid="environment-logs"
        >
            {(error || loadingMessage) && (
                <div className="absolute bottom-0 flex w-full flex-col items-center p-4">
                    <ErrorMessage error={error} />
                    {loadingMessage && (
                        <span className="text-sm" data-testid="logs-loading-message">
                            {loadingMessage}
                        </span>
                    )}
                </div>
            )}
            <div className={cn(className, "min-w-0 grow")} ref={logContainerRef}>
                <TreeList<LogTreeNodeData>
                    ref={treeListRef}
                    nodes={treeNodes}
                    height={availableHeight}
                    width={availableWidth}
                    className="overflow-hidden"
                    itemHeight={(index, cellProps) => {
                        const node = cellProps.flatNodes[index];
                        return node.data?.type === "logGroup" ? 36 : 16;
                    }}
                    useDynamicHeight={true}
                    overscanCount={overscanCount}
                    initialExpandedNodeIds={initialExpandedNodeIds}
                    renderNode={(node, updateDynamicHeight) => (
                        <LogTreeNodeRenderer
                            node={node}
                            copiedLineIndex={copiedLineIndex}
                            copiedGroupId={copiedGroupId}
                            onCopyLine={handleCopyLine}
                            onCopyGroup={handleCopyGroup}
                            updateDynamicHeight={updateDynamicHeight}
                        />
                    )}
                    onNodesRendered={handleOnRowsRendered}
                />
            </div>
        </div>
    );
};

type LogTreeNodeRendererProps = {
    node: FlatTreeNode<LogTreeNodeData>;
    copiedLineIndex: string | null;
    copiedGroupId: string | null;
    onCopyLine: (line: string, nodeId: string) => void;
    onCopyGroup: (validLines: (SectionLogLine | LegacyLogLine)[], groupId: string) => void;
    updateDynamicHeight: (height: number) => void;
};

const LogTreeNodeRenderer: FC<LogTreeNodeRendererProps> = ({
    node,
    copiedLineIndex,
    copiedGroupId,
    onCopyLine,
    onCopyGroup,
    updateDynamicHeight,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (updateDynamicHeight && ref.current && node.data?.type === "logLine") {
            const height = ref.current.clientHeight;
            updateDynamicHeight(height);
        }
    });

    if (node.data?.type === "logLine") {
        const { line } = node.data;
        const isCopied = copiedLineIndex === node.id;
        const emptyLine = line.line.trim() === "";

        return (
            <div ref={ref} className="group flex min-w-0 grow items-center justify-between">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    {line.timestamp && (
                        <span className="flex-shrink-0 whitespace-nowrap font-mono text-sm text-gray-500">
                            {formatMediumTime(line.timestamp)}
                        </span>
                    )}
                    <span
                        className="min-w-0 grow whitespace-pre text-wrap break-words font-mono text-sm"
                        dangerouslySetInnerHTML={{ __html: Anser.ansiToHtml(line.line) }}
                    />
                </div>
                {!emptyLine && (
                    <Tooltip content="Copy to clipboard" usePortal={true}>
                        <Button
                            variant="ghost"
                            size="xs"
                            className={cn(
                                "right-2 size-4 border-0 text-always-light opacity-0 hover:text-always-light group-hover:opacity-100",
                                {
                                    "disabled:text-green-500": isCopied,
                                },
                            )}
                            LeadingIcon={isCopied ? IconCheck : IconCopy}
                            disabled={isCopied}
                            onClick={(e) => {
                                e.stopPropagation();
                                onCopyLine(line.line, node.id);
                            }}
                            aria-label="Copy to clipboard"
                            data-tracking-id="copy-to-clipboard-environment-logs"
                        />
                    </Tooltip>
                )}
            </div>
        );
    }

    if (node.data?.type === "logGroup") {
        const { logGroup, validLines } = node.data;
        const isCopied = copiedGroupId === node.id;

        let icon: ReactNode;
        if (logGroup.outcome === undefined && !logGroup.continuous) {
            icon = <IconSpinner size="lg" className="animate-spin text-content-yield" />;
        } else if (logGroup.outcome === undefined && logGroup.continuous) {
            icon = <IconDotLarge size="lg" className="animate-pulse text-content-positive" />;
        } else if (logGroup.outcome === LogSectionOutcome.SUCCESS) {
            icon = <IconCheckCircle size="lg" className="text-content-positive" />;
        } else if (logGroup.outcome === LogSectionOutcome.CANCELLED) {
            icon = (
                <Tooltip content="This action was cancelled">
                    <div>
                        <IconWarningCircle size="lg" className="text-content-tertiary dark:text-content-secondary" />
                    </div>
                </Tooltip>
            );
        } else {
            icon = <IconWarningCircleFilled size="lg" className="text-content-negative" />;
        }

        return (
            <div ref={ref} className="flex min-w-0 grow items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="flex min-h-6 min-w-6 flex-shrink-0 items-center justify-center">{icon}</div>
                    <span className="truncate font-mono text-sm text-always-light">{logGroup.title}</span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                    {logGroup.secondsElapsed && (
                        <span className="font-mono text-sm text-always-light">{duration(logGroup.secondsElapsed)}</span>
                    )}
                    <Tooltip content="Copy all lines in this group">
                        <Button
                            variant="ghost"
                            size="xs"
                            className={cn("size-5 border-0 text-always-light hover:text-always-light", {
                                "disabled:text-green-500": isCopied,
                            })}
                            LeadingIcon={isCopied ? IconCheck : IconCopy}
                            disabled={isCopied}
                            onClick={(e) => {
                                e.stopPropagation();
                                onCopyGroup(validLines, node.id);
                            }}
                            aria-label="Copy all lines"
                            data-tracking-id="copy-all-lines"
                        />
                    </Tooltip>
                </div>
            </div>
        );
    }

    return (
        <div ref={ref}>
            <span className="text-sm">{node.label}</span>
        </div>
    );
};
